import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Button, ConfirmModal } from "@libs/rubick";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useApi } from "../../../app/apiContext";
export function CreateClusterModal({ isOpen, onClose, controlPlaneUrl }) {
    const [modalStep, setModalStep] = useState({
        step: "form"
    });
    const modalStepRef = useRef(modalStep);
    modalStepRef.current = modalStep;
    const { clustersApi, statusApi } = useApi();
    const [createCluster, { isLoading }] = clustersApi.useCreateClusterMutation();
    const { refetch } = statusApi.useGetNodeConfigQuery();
    const handleContinue = ()=>{
        setModalStep({
            step: "confirm"
        });
    };
    const handleConfirmCreate = async ()=>{
        try {
            const result = await createCluster({
                description: null
            }).unwrap();
            setModalStep({
                step: "result",
                status: "success",
                message: result.message || "Cluster created successfully! This node is now a control plane."
            });
        } catch (error) {
            setModalStep({
                step: "result",
                status: "error",
                message: error?.data?.message || "Failed to create cluster: An error occurred while initializing the control plane"
            });
        }
    };
    const handleClose = ()=>{
        onClose();
    };
    const handleAfterLeave = ()=>{
        const wasSuccessful = modalStepRef.current.step === "result" && modalStepRef.current.status === "success";
        setModalStep({
            step: "form"
        });
        if (wasSuccessful) {
            refetch();
        }
    };
    const confirmMessage = `This action is irreversible. This node will become a control plane node in a Qrypt Platform cluster.\n\nControl Plane URL: ${controlPlaneUrl}`;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Transition, {
                appear: true,
                show: isOpen && modalStep.step === "form",
                children: /*#__PURE__*/ _jsxs(Dialog, {
                    className: "relative z-50",
                    onClose: handleClose,
                    children: [
                        /*#__PURE__*/ _jsx(TransitionChild, {
                            enter: "ease-out duration-300",
                            enterFrom: "opacity-0",
                            enterTo: "opacity-100",
                            leave: "ease-in duration-200",
                            leaveFrom: "opacity-100",
                            leaveTo: "opacity-0",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "fixed inset-0 bg-black bg-opacity-50"
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "fixed inset-0 overflow-y-auto",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "flex min-h-full items-center justify-center p-4 text-center",
                                children: /*#__PURE__*/ _jsx(TransitionChild, {
                                    enter: "ease-out duration-300",
                                    enterFrom: "opacity-0 scale-95",
                                    enterTo: "opacity-100 scale-100",
                                    leave: "ease-in duration-200",
                                    leaveFrom: "opacity-100 scale-100",
                                    leaveTo: "opacity-0 scale-95",
                                    children: /*#__PURE__*/ _jsxs(DialogPanel, {
                                        className: "w-full max-w-md transform overflow-visible rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between mb-6",
                                                children: [
                                                    /*#__PURE__*/ _jsx(DialogTitle, {
                                                        as: "h3",
                                                        className: "text-xl font-semibold text-gray-900",
                                                        children: "Create Cluster"
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        onClick: handleClose,
                                                        className: "p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100",
                                                        children: /*#__PURE__*/ _jsx(XMarkIcon, {
                                                            className: "h-5 w-5"
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "space-y-6",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "bg-gray-50 rounded-lg p-4",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm text-gray-600 mb-4",
                                                                children: "The URL of the control plane endpoints will be:"
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm font-mono text-gray-700",
                                                                children: controlPlaneUrl
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex justify-end space-x-2",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "simple",
                                                                color: "red",
                                                                onClick: handleClose,
                                                                children: "Cancel"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "simple",
                                                                color: "navy-90",
                                                                onClick: handleContinue,
                                                                children: "Continue"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                })
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isOpen && modalStep.step === "confirm",
                onClose: ()=>setModalStep({
                        step: "form"
                    }),
                onConfirm: handleConfirmCreate,
                title: "Warning",
                message: confirmMessage,
                confirmText: "Create Cluster",
                cancelText: "Cancel",
                variant: "danger",
                closeOnConfirm: false,
                isLoading: isLoading
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isOpen && modalStep.step === "result",
                onClose: handleClose,
                onConfirm: handleClose,
                title: "Create Cluster",
                message: "",
                success: modalStep.step === "result" && modalStep.status === "success",
                successMessage: modalStep.step === "result" ? modalStep.message : "",
                error: modalStep.step === "result" && modalStep.status === "error" ? modalStep.message : null,
                afterLeave: handleAfterLeave
            })
        ]
    });
}
